import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiUrl } from '@/lib/api'
import type { Booking, BookingStatus } from '@brochure-cloudflare/shared'

const statusLabels: Record<BookingStatus, string> = {
  pending: 'In attesa',
  confirmed: 'Confermata',
  cancelled: 'Annullata',
}

export function AdminDashboard() {
  const { token, logout } = useAuth()
  const navigate = useNavigate()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchBookings()
  }, [])

  const fetchBookings = async () => {
    try {
      const res = await fetch(apiUrl('/api/bookings'), {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.status === 401) {
        logout()
        navigate('/admin/login')
        return
      }

      if (!res.ok) throw new Error('Failed to load bookings')

      const data = (await res.json()) as { bookings: Booking[] }
      setBookings(data.bookings)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore di caricamento')
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (id: number, status: BookingStatus) => {
    const res = await fetch(apiUrl(`/api/bookings/${id}`), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    })

    if (!res.ok) {
      const data = await res.json()
      alert(data.error || 'Errore aggiornamento')
      return
    }

    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, status } : b)),
    )
  }

  const deleteBooking = async (id: number) => {
    if (!confirm('Eliminare questa prenotazione?')) return

    const res = await fetch(apiUrl(`/api/bookings/${id}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const data = await res.json()
      alert(data.error || 'Errore eliminazione')
      return
    }

    setBookings((prev) => prev.filter((b) => b.id !== id))
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        Caricamento...
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Prenotazioni</h1>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Logout
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-800 rounded-md">{error}</div>
        )}

        {bookings.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Nessuna prenotazione trovata.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="p-4 font-medium">ID</th>
                  <th className="p-4 font-medium">Nome</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Telefono</th>
                  <th className="p-4 font-medium">Servizio</th>
                  <th className="p-4 font-medium">Data preferita</th>
                  <th className="p-4 font-medium">Stato</th>
                  <th className="p-4 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="p-4">{booking.id}</td>
                    <td className="p-4">{booking.name}</td>
                    <td className="p-4">{booking.email}</td>
                    <td className="p-4">{booking.phone || '-'}</td>
                    <td className="p-4">{booking.service || '-'}</td>
                    <td className="p-4">
                      {booking.preferredDate || '-'}{' '}
                      {booking.preferredTime || ''}
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : booking.status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {statusLabels[booking.status]}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <select
                          value={booking.status}
                          onChange={(e) =>
                            updateStatus(booking.id, e.target.value as BookingStatus)
                          }
                          className="text-sm border border-gray-300 rounded-md px-2 py-1"
                        >
                          <option value="pending">{statusLabels.pending}</option>
                          <option value="confirmed">{statusLabels.confirmed}</option>
                          <option value="cancelled">{statusLabels.cancelled}</option>
                        </select>
                        <button
                          onClick={() => deleteBooking(booking.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Elimina
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
